import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { modalStyles } from './styles'
import {
  Container,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@mui/material'
import LoadingIndicator from '../../common/LoadingIndicator'
import Button from '../../common/Button'
import useFetch from '../../hooks/useFetch'
import { urlApi } from '../../config'

export default function Coupons() {
  const { loading, data } = useFetch(`${urlApi}/admin/coupons`)
  const [activeCoupons, setActiveCoupons] = useState([])
  const [inactiveCoupons, setInactiveCoupons] = useState([])
  const [createModal, setCreateModal] = useState(false)
  const [tag, setTag] = useState('all')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('active')

  useEffect(() => {
    if (!loading && data?.length > 0) {
      setActiveCoupons(
        data
          .filter(coupon => !coupon.disabled)
          .sort((a, b) => new Date(b.created) - new Date(a.created))
      )
      setInactiveCoupons(
        data
          .filter(coupon => coupon.disabled)
          .sort((a, b) => new Date(b.created) - new Date(a.created))
      )
    }
  }, [loading, data])

  useEffect(() => {
    if (!data) return
    const matchesTag = coupon => (tag === 'all' ? true : coupon.tag === tag)
    const matchesSearch = coupon =>
      search.trim().length === 0
        ? true
        : (coupon.code || '').toLowerCase().includes(search.trim().toLowerCase())

    setActiveCoupons(
      data
        .filter(coupon => !coupon.disabled && matchesTag(coupon) && matchesSearch(coupon))
        .sort((a, b) => new Date(b.created) - new Date(a.created))
    )
    setInactiveCoupons(
      data
        .filter(coupon => coupon.disabled && matchesTag(coupon) && matchesSearch(coupon))
        .sort((a, b) => new Date(b.created) - new Date(a.created))
    )
  }, [tag, search, data])

  const displayCoupons =
    status === 'all'
      ? [...activeCoupons, ...inactiveCoupons]
      : status === 'active'
      ? activeCoupons
      : inactiveCoupons

  return (
    <>
      <Container
        maxWidth={false}
        sx={{
          height: 'calc(100vh - 4rem)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: '100%',
          maxWidth: '100%'
        }}
      >
        {/* Filters */}
        <div className='bg-white dark:bg-dk-surface rounded-xl shadow-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-4'>
          <div className='flex flex-col'>
            <span className='text-sm text-gray-500 dark:text-gray-400 mb-1'>Buscar</span>
            <TextField
              size='small'
              placeholder='Buscar cupón'
              value={search}
              onChange={evt => setSearch(evt.target.value)}
            />
          </div>
          <div className='flex flex-col'>
            <span className='text-sm text-gray-500 dark:text-gray-400 mb-1'>Tipo</span>
            <Tag filter tag={tag} setTag={setTag} />
          </div>
          <div className='flex flex-col'>
            <span className='text-sm text-gray-500 dark:text-gray-400 mb-1'>Estado</span>
            <Select size='small' value={status} onChange={evt => setStatus(evt.target.value)}>
              <MenuItem value={'all'}>Todos</MenuItem>
              <MenuItem value={'active'}>Activos</MenuItem>
              <MenuItem value={'inactive'}>Inactivos</MenuItem>
            </Select>
          </div>
          <div className='flex items-end'>
            <Button
              style={{ borderRadius: '10px', paddingTop: '6px', paddingBottom: '6px' }}
              name={'Crear cupón'}
              onClick={() => setCreateModal(true)}
            />
          </div>
        </div>

        {/* Table */}
        <div className='bg-white dark:bg-dk-surface rounded-xl shadow-sm w-full'>
          <TableContainer
            sx={{ maxHeight: 'calc(100vh - 4rem - 160px)' }}
            className='overflow-x-auto w-full'
          >
            <Table size='small' className='text-sm' sx={{ width: '100%' }}>
              <TableHead className='bg-gray-50 dark:bg-dk-raised'>
                <TableRow>
                  <TableCell className='text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300'>
                    ON/OFF
                  </TableCell>
                  <TableCell className='text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300'>
                    Nombre
                  </TableCell>
                  <TableCell className='text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300'>
                    Descuento
                  </TableCell>
                  <TableCell className='text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300'>
                    Fecha creado
                  </TableCell>
                  <TableCell className='text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300'>
                    Tipo
                  </TableCell>
                  <TableCell className='text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300'>
                    Usado
                  </TableCell>
                  <TableCell className='text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300'>
                    Cohort ID / User Id
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className='py-10'>
                      <div className='flex justify-center'>
                        <LoadingIndicator />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : displayCoupons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className='px-4 py-6 text-gray-500 dark:text-gray-400'>
                      Sin resultados
                    </TableCell>
                  </TableRow>
                ) : (
                  (status === 'all'
                    ? [...activeCoupons, ...inactiveCoupons].sort(
                        (a, b) => new Date(b.created) - new Date(a.created)
                      )
                    : displayCoupons
                  ).map(coupon => {
                    const date = new Date(coupon.created).toLocaleDateString()
                    const owner = coupon.whitelist || coupon.user_id || coupon.userId || '-'
                    return (
                      <TableRow key={coupon._id || coupon.code} className='border-t dark:border-dk-border'>
                        <TableCell className='px-4 py-3 whitespace-nowrap'>
                          <RowToggle
                            coupon={coupon}
                            setActiveCoupons={setActiveCoupons}
                            setInactiveCoupons={setInactiveCoupons}
                          />
                        </TableCell>
                        <TableCell className='px-4 py-3 whitespace-nowrap font-semibold'>
                          {coupon.code}
                        </TableCell>
                        <TableCell className='px-4 py-3 whitespace-nowrap'>
                          {coupon.discount}%
                        </TableCell>
                        <TableCell className='px-4 py-3 whitespace-nowrap'>{date}</TableCell>
                        <TableCell className='px-4 py-3 whitespace-nowrap'>
                          {tagToLabel(coupon.tag)}
                        </TableCell>
                        <TableCell className='px-4 py-3 whitespace-nowrap'>{coupon.used}</TableCell>
                        <TableCell className='px-4 py-3 whitespace-nowrap'>{owner}</TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Container>
      {createModal && (
        <CreateModal setActiveCoupons={setActiveCoupons} setCreateModal={setCreateModal} />
      )}
    </>
  )
}

const CouponCard = ({ coupon }) => {
  const [active, setActive] = useState(!coupon.disabled)
  const [update, setUpdate] = useState(false)
  const date = new Date(coupon.created).toLocaleDateString()

  const handleToggle = () => {
    if (window.confirm('¿Activar/Desactivar cupón?')) {
      setUpdate(true)
    }
  }

  return (
    <div className='w-full rounded-[12px] flex flex-col p-6 bg-white dark:bg-dk-surface shadow gap-8 mb-3'>
      <div className='flex justify-between items-center w-full'>
        <p className='max-w-2/3 font-bold text-lg'>{coupon.code}</p>
        <p className='font-bold'>{date}</p>
      </div>
      <div className='flex flex-row w-full justify-between'>
        <div className='flex flex-col items-center text-lg'>
          <p className='font-bold'>Descuento</p>
          <p>{coupon.discount}%</p>
        </div>
        <div className='flex flex-col items-center text-lg'>
          <p className='font-bold'>Usado</p>
          <p>{coupon.used}</p>
        </div>
      </div>
      <div className='w-full flex items-end justify-between'>
        <p className='text-lg font-bold'>{tagToLabel(coupon.tag)}</p>
        {update ? (
          <Update id={coupon._id} active={active} setUpdate={setUpdate} setActive={setActive} />
        ) : (
          <ToggleSwitch checked={active} handleToggle={handleToggle} />
        )}
      </div>
    </div>
  )
}

const ToggleSwitch = ({ checked, handleToggle }) => {
  return (
    <label className={'relative inline-flex items-center cursor-pointer'}>
      <div
        onClick={handleToggle}
        className={`ring-0 rounded-full ${
          checked ? 'bg-light_coral after:bg-[#fff] after:translate-x-[19px]' : 'bg-[#E8E8E8] dark:bg-dk-hover'
        }  after:bg-[#ABABAB] outline-none duration-300 after:duration-300 w-[50px] h-[30px] shadow-md peer-focus:outline-none after:rounded-full after:absolute after:outline-none after:h-[20px] after:w-[20px] after:top-[5px] after:left-[6px] after:flex after:justify-center after:items-center peer-hover:after:scale-95 after:content-[""]`}
      />
    </label>
  )
}

const CreateModal = ({ setActiveCoupons, setCreateModal }) => {
  const [code, setCode] = useState('')
  const [discount, setDiscount] = useState()
  const [tag, setTag] = useState('compensation')
  const [reusable, setReusable] = useState(false)
  const [userId, setUserId] = useState('')
  const [whitelist, setWhitelist] = useState('')
  const [create, setCreate] = useState(false)

  const handleClose = () => {
    setCreateModal(false)
  }

  const handleSuccess = data => {
    setActiveCoupons(prev => [data, ...prev])
    setCreate(false)
    setCreateModal(false)
  }

  const handleError = () => {
    alert('Hubo un error')
    setCreate(false)
  }

  return (
    <Modal style={modalStyles} isOpen={true} onRequestClose={handleClose}>
      <div className='flex flex-col justify-between h-full gap-6'>
        <p className='text-xl font-bold'>Crear cupón</p>
        <TextField
          size='small'
          label='Código'
          value={code}
          inputProps={{ autoCapitalize: 'characters' }}
          onChange={evt => setCode(evt.target.value)}
        />
        <TextField
          size='small'
          type='number'
          label='Descuento (porcentaje)'
          value={discount}
          onChange={evt => setDiscount(evt.target.value)}
        />
        <Tag tag={tag} setTag={setTag} />
        {tag === 'compensation' ? (
          <TextField
            size='small'
            label='Usuario (Id)'
            value={userId}
            onChange={evt => setUserId(evt.target.value)}
          />
        ) : (
          <TextField
            size='small'
            label='Whitelist (Cohort Id)'
            value={whitelist}
            onChange={evt => setWhitelist(evt.target.value)}
          />
        )}
        <div className='flex flex-row gap-2 items-center'>
          <ToggleSwitch checked={reusable} handleToggle={() => setReusable(!reusable)} />
          <p>Reusable</p>
        </div>
        {create ? (
          <div className='flex w-full items-center justify-center'>
            <Create
              body={{ code, discount, tag, reusable, userId, whitelist }}
              handleSuccess={handleSuccess}
              handleError={handleError}
            />
          </div>
        ) : (
          <button
            className={`bg-light_coral text-white rounded-lg p-2 ${!code && 'bg-gray'}`}
            onClick={() => setCreate(true)}
            disabled={!code || !discount}
          >
            Crear
          </button>
        )}
      </div>
    </Modal>
  )
}

const Create = ({ body, handleSuccess, handleError }) => {
  const { loading, data, responseOk } = useFetch(`${urlApi}/admin/coupons`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      ...body,
      code: body.code.trim(),
      whitelist: body.whitelist.trim().length > 0 ? body.whitelist : null,
      user_id: body.userId.trim().length > 0 ? body.userId : null
    })
  })

  useEffect(() => {
    if (responseOk && !Array.isArray(data)) {
      handleSuccess(data)
    } else if (!loading && !responseOk) {
      handleError()
    }
  }, [loading, responseOk, data])

  return <LoadingIndicator />
}

const Update = ({ id, active, setUpdate, setActive }) => {
  const { loading, responseOk } = useFetch(`${urlApi}/admin/coupons/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      disabled: active
    })
  })

  useEffect(() => {
    if (responseOk) {
      setActive(!active)
      setUpdate(false)
    } else if (!loading) {
      alert('Hubo un error')
      setUpdate(false)
    }
  }, [loading, responseOk])

  return <LoadingIndicator />
}

const Tag = ({ tag, setTag, filter }) => {
  return (
    <Select value={tag} size='small' onChange={evt => setTag(evt.target.value)}>
      {filter && <MenuItem value={'all'}>Todos</MenuItem>}
      <MenuItem value={'compensation'}>Compensación</MenuItem>
      <MenuItem value={'promotion'}>Promoción</MenuItem>
      <MenuItem value={'alliance'}>Alianza</MenuItem>
      <MenuItem value={'influencer'}>Influencer</MenuItem>
      <MenuItem value={'other'}>Otro</MenuItem>
    </Select>
  )
}

const tagToLabel = tag => {
  switch (tag) {
    case 'promotion':
      return 'Promoción'
    case 'compensation':
      return 'Compensación'
    case 'alliance':
      return 'Alianza'
    case 'influencer':
      return 'Influencer'
    case 'other':
      return 'Otro'
    default:
      return 'Ninguno'
  }
}

const RowToggle = ({ coupon, setActiveCoupons, setInactiveCoupons }) => {
  const [active, setActive] = useState(!coupon.disabled)
  const [updating, setUpdating] = useState(false)

  const onToggle = () => {
    if (window.confirm('¿Activar/Desactivar cupón?')) setUpdating(true)
  }

  useEffect(() => {
    if (!updating) return
  }, [updating])

  return updating ? (
    <ToggleUpdate
      id={coupon._id}
      active={active}
      setActive={setActive}
      setUpdating={setUpdating}
      setActiveCoupons={setActiveCoupons}
      setInactiveCoupons={setInactiveCoupons}
      coupon={coupon}
    />
  ) : (
    <ToggleSwitch checked={active} handleToggle={onToggle} />
  )
}

const ToggleUpdate = ({
  id,
  active,
  setActive,
  setUpdating,
  setActiveCoupons,
  setInactiveCoupons,
  coupon
}) => {
  const { loading, responseOk } = useFetch(`${urlApi}/admin/coupons/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      disabled: active
    })
  })

  useEffect(() => {
    if (responseOk) {
      const nextActive = !active
      setActive(nextActive)
      setUpdating(false)
      // move coupon between lists
      if (nextActive) {
        // became active -> remove from inactive and add to active
        setInactiveCoupons(prev => prev.filter(c => c._id !== coupon._id))
        setActiveCoupons(prev => [{ ...coupon, disabled: false }, ...prev])
      } else {
        // became inactive -> remove from active and add to inactive
        setActiveCoupons(prev => prev.filter(c => c._id !== coupon._id))
        setInactiveCoupons(prev => [{ ...coupon, disabled: true }, ...prev])
      }
    } else if (!loading) {
      alert('Hubo un error')
      setUpdating(false)
    }
  }, [loading, responseOk])

  return <LoadingIndicator />
}
